import { useMutation } from 'urql';
import StickyActions from '@/components/StickyActions.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { CONFIRMAR_ASIENTO, ANULAR_ASIENTO, GET_ASIENTO } from './gql.js';
import { useQuery } from 'urql';

export default function AsientoDetail({ asientoId, onUpdated, onEdit }) {
  const toast = useToast();
  const [{ data, fetching, error }, reexecute] = useQuery({ query: GET_ASIENTO, variables: { id: asientoId }, pause: !asientoId });
  const [, confirmarAsiento] = useMutation(CONFIRMAR_ASIENTO);
  const [, anularAsiento] = useMutation(ANULAR_ASIENTO);

  if (!asientoId) {
    return (
      <div className="rounded-2xl border border-dashed border-[rgb(var(--border))] bg-[rgba(var(--panel),0.45)] p-6 text-center text-sm text-[rgb(var(--muted))]">
        Seleccioná un asiento para ver el detalle.
      </div>
    );
  }

  if (fetching) {
    return <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-[rgb(var(--muted))]">Cargando asiento...</div>;
  }

  if (error) {
    return <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-red-500">{error.message}</div>;
  }

  const asiento = data?.getAsiento;
  if (!asiento) {
    return null;
  }

  const handleConfirmar = async () => {
    try {
      const result = await confirmarAsiento({ id: asiento.id });
      if (result.error) throw result.error;
      toast.success('Asiento confirmado');
      reexecute({ requestPolicy: 'network-only' });
      onUpdated?.();
    } catch (err) {
      toast.error(err.message ?? 'No fue posible confirmar el asiento');
    }
  };

  const handleAnular = async () => {
    const motivo = window.prompt('Ingresá un motivo para la anulación', 'Anulación manual');
    if (motivo === null) return;
    try {
      const result = await anularAsiento({ id: asiento.id, motivo });
      if (result.error) throw result.error;
      toast.success('Asiento anulado');
      onUpdated?.();
      reexecute({ requestPolicy: 'network-only' });
    } catch (err) {
      toast.error(err.message ?? 'No fue posible anular el asiento');
    }
  };

  return (
    <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_20px_36px_-24px_rgba(var(--shadow),0.35)]">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-[rgb(var(--muted))]">Asiento #{asiento.numero}</p>
          <h2 className="text-xl font-semibold text-[rgb(var(--fg))]">{asiento.fecha} · {asiento.glosa || 'Sin glosa'}</h2>
          <p className="text-sm text-[rgb(var(--muted))]">
            Estado: <span className="font-medium text-[rgb(var(--fg))]">{asiento.estado}</span> · Origen: {asiento.origen}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-[rgb(var(--muted))]">
          <span>Periodo: {asiento.periodo}</span>
          <span>Diferencia: {asiento.totales?.diferencia?.toFixed(2)}</span>
        </div>
      </header>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="border-b border-[rgb(var(--border))] text-[rgb(var(--muted))]">
            <tr>
              <th className="px-3 py-2 text-left">Cuenta</th>
              <th className="px-3 py-2 text-left">Centro</th>
              <th className="px-3 py-2 text-left">Descripción</th>
              <th className="px-3 py-2 text-right">Debe</th>
              <th className="px-3 py-2 text-right">Haber</th>
              <th className="px-3 py-2 text-left">Moneda</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgb(var(--border))]">
            {asiento.detalle?.map((linea) => (
              <tr key={linea.id}>
                <td className="px-3 py-2 font-medium text-[rgb(var(--fg))]">
                  {linea.cuentaCodigo} · {linea.cuentaNombre}
                </td>
                <td className="px-3 py-2 text-[rgb(var(--muted))]">{linea.centroNombre || '—'}</td>
                <td className="px-3 py-2">{linea.descripcion || '—'}</td>
                <td className="px-3 py-2 text-right text-[rgb(var(--fg))]">{Number(linea.debe ?? 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-[rgb(var(--fg))]">{Number(linea.haber ?? 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-[rgb(var(--muted))]">{linea.moneda}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)]">
            <tr>
              <td className="px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]" colSpan={3}>
                Totales
              </td>
              <td className="px-3 py-2 text-right text-sm font-semibold text-[rgb(var(--fg))]">
                {Number(asiento.totales?.debe ?? 0).toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right text-sm font-semibold text-[rgb(var(--fg))]">
                {Number(asiento.totales?.haber ?? 0).toFixed(2)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <StickyActions
        left={<div className="text-sm text-[rgb(var(--muted))]">Creado {formatDate(asiento.createdAt)} · Actualizado {formatDate(asiento.updatedAt)}</div>}
        right={
          <div className="flex items-center gap-2">
            {asiento.estado === 'borrador' ? (
              <button
                type="button"
                onClick={() => onEdit?.(asiento.id)}
                className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Editar
              </button>
            ) : null}
            {asiento.estado === 'borrador' ? (
              <button
                type="button"
                onClick={handleConfirmar}
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
              >
                Confirmar
              </button>
            ) : null}
            {asiento.estado === 'confirmado' ? (
              <button
                type="button"
                onClick={handleAnular}
                className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Anular
              </button>
            ) : null}
          </div>
        }
      />
    </section>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
}
